import React from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import api from '../../api';
import {useIsMounted, useStatus} from '../../utils/hooks';
import {getErrorMessage} from '../../utils/common';
import {GUEST_APP_EVENTS_NAME, PATTERNS} from '../../utils/constants';
import addEmailIcon from '../../assets/img/icon-add-email.png';
import Modal from '../Modal';
import {Button} from '../Button';
import {Form, ButtonsWrapper, InputWrapper, ModalInput} from './styled';
import useMixPanel from 'hooks/useMixPanel';

enum FormNames {
  email = 'email',
}

type FormTypes = {
  [FormNames.email]: string;
};

type EmailRequestModalProps = {
  onClose: () => void;
  guestId: string;
  mutateGuest: (data: any, shouldRevalidate: boolean) => Promise<void>;
  enterGuestMailRef: {current: string | null};
  onSuccess: () => void;
};

function EmailRequestModal({
  onClose,
  guestId,
  mutateGuest,
  onSuccess,
  enterGuestMailRef,
}: EmailRequestModalProps) {
  const {t} = useTranslation();
  const {
    register,
    formState: {errors},
    handleSubmit,
  } = useForm<FormTypes>();
  const isMounted = useIsMounted();
  const {isLoading, setStatus} = useStatus();
  const [errorMessage, setErrorMessage] = React.useState('');
  const {IdGuestAppEvents} = useMixPanel();

  const getPayload = (data: FormTypes) => {
    return {
      email: data[FormNames.email],
    };
  };

  const onSubmit = async (data: FormTypes) => {
    const payload = getPayload(data);
    setStatus('loading');
    setErrorMessage('');

    const {error, data: guest} = await api.guests.patch(guestId, payload);

    if (!isMounted.current) {
      return;
    }

    if (guest) {
      enterGuestMailRef.current = payload.email;
      onClose();
      await mutateGuest(guest, false);
      onSuccess();
      IdGuestAppEvents(GUEST_APP_EVENTS_NAME.dealRequested);
    }

    if (error) {
      const message = getErrorMessage(error);
      setErrorMessage(message);
    }
    setStatus('idle');
  };

  return (
    <Modal
      open
      closeOnEscape={!isLoading}
      iconProps={{
        src: addEmailIcon,
        height: 110,
        width: 110,
        alt: '',
      }}
      onClose={onClose}
      title={t('add_your_email_title')}
      text={t('add_your_email_text')}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <InputWrapper>
          <ModalInput
            disabled={isLoading}
            {...register(FormNames.email, {
              required: t('required') as string,
              pattern: {
                value: PATTERNS.email,
                message: t('invalid_email'),
              },
            })}
            error={errors[FormNames.email]?.message || errorMessage}
            placeholder={t('enter_email')}
          />
        </InputWrapper>
        <ButtonsWrapper>
          <Button type="submit" label={t('continue')} disabled={isLoading} />
          <Button
            secondary
            label={t('back_button')}
            onClick={onClose}
            disabled={isLoading}
          />
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
}

export {EmailRequestModal};
